/**
 * Translation Loader Service
 *
 * Handles loading translation overrides from database and caching them locally.
 * Uses 24-hour cache strategy for optimal performance.
 */

import { supabase } from './supabaseClient';
import { systemLogger } from './logging';

interface TranslationCache {
  data: Record<string, string>;
  timestamp: number;
  version: number;
  language: string;
}

interface TranslationOverridesResponse {
  data: Record<string, string>;
  metadata: {
    language: string;
    count: number;
    timestamp: string;
  };
  success: boolean;
}

const CACHE_KEY_PREFIX = 'translation_overrides_';
const CACHE_VERSION = 1;
const CACHE_TTL_MS = 24 * 60 * 60 * 1000; // 24 hours in milliseconds

/**
 * Normalize language code to base language (remove region suffix)
 *
 * Ensures consistent language codes across the application regardless of
 * browser detection. For example:
 * - "en-US" → "en"
 * - "es-MX" → "es"
 * - "fr-CA" → "fr"
 * - "en" → "en" (already normalized)
 *
 * @param languageCode - ISO language code (may include region)
 * @returns Base language code without region suffix
 */
function normalizeLanguageCode(languageCode: string): string {
  const parts = languageCode.split('-');
  return parts[0] || 'en'; // Fallback to 'en' if split somehow produces empty array
}

/**
 * Get cache key for a specific language
 */
function getCacheKey(languageCode: string): string {
  return `${CACHE_KEY_PREFIX}${languageCode}_v${CACHE_VERSION}`;
}

/**
 * Check if cached data is still valid (not expired)
 */
function isCacheValid(cache: TranslationCache | null): boolean {
  if (!cache || cache.version !== CACHE_VERSION) {
    return false;
  }

  const now = Date.now();
  const age = now - cache.timestamp;

  return age < CACHE_TTL_MS;
}

/**
 * Get translation overrides from localStorage cache
 */
function getCachedOverrides(languageCode: string): Record<string, string> | null {
  try {
    const cacheKey = getCacheKey(languageCode);
    const cachedData = localStorage.getItem(cacheKey);

    if (!cachedData) {
      systemLogger.debug('No cached translation overrides found', { languageCode });
      return null;
    }

    const cache: TranslationCache = JSON.parse(cachedData);

    if (!isCacheValid(cache)) {
      systemLogger.debug('Cached translation overrides expired', {
        languageCode,
        age: Date.now() - cache.timestamp,
        ttl: CACHE_TTL_MS,
      });
      // Clear expired cache
      localStorage.removeItem(cacheKey);
      return null;
    }

    systemLogger.debug('Using cached translation overrides', {
      languageCode,
      count: Object.keys(cache.data).length,
      age: Date.now() - cache.timestamp,
    });

    return cache.data;
  } catch (error) {
    systemLogger.error('Failed to read translation overrides from cache', {
      error,
      languageCode,
    });
    return null;
  }
}

/**
 * Save translation overrides to localStorage cache
 */
function setCachedOverrides(
  languageCode: string,
  overrides: Record<string, string>
): void {
  try {
    const cache: TranslationCache = {
      data: overrides,
      timestamp: Date.now(),
      version: CACHE_VERSION,
      language: languageCode,
    };

    const cacheKey = getCacheKey(languageCode);
    localStorage.setItem(cacheKey, JSON.stringify(cache));

    systemLogger.debug('Cached translation overrides', {
      languageCode,
      count: Object.keys(overrides).length,
    });
  } catch (error) {
    systemLogger.error('Failed to cache translation overrides', {
      error,
      languageCode,
    });
  }
}

/**
 * Fetch translation overrides from database via edge function
 *
 * CRITICAL FIX: Added fast-fail and timeout protection to prevent 3-minute hang
 * when auth token is not available (e.g., during initial app load before auth context initializes)
 */
async function fetchOverridesFromDatabase(
  languageCode: string
): Promise<Record<string, string>> {
  const FETCH_TIMEOUT_MS = 5000; // 5 seconds max - fail fast

  try {
    systemLogger.debug('Fetching translation overrides from database', {
      languageCode,
    });

    // Get Supabase session for authentication
    const { data: sessionData } = await supabase.auth.getSession();
    const token = sessionData.session?.access_token;

    // CRITICAL: Fast-fail if no token available (e.g., during initial app load)
    if (!token) {
      systemLogger.debug('No auth token available, skipping translation override fetch', {
        languageCode,
        reason: 'Auth context not initialized yet'
      });
      return {}; // Early return - don't attempt fetch without token
    }

    // Build edge function URL with query parameter
    const supabaseUrl = import.meta.env['VITE_PUBLIC_SUPABASE_URL'];
    const cacheBuster = `_t=${Date.now()}`;
    const url = `${supabaseUrl}/functions/v1/get-translations?language_code=${languageCode}&${cacheBuster}`;

    // Race fetch against timeout to prevent indefinite hangs
    const fetchPromise = fetch(url, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    const timeoutPromise = new Promise<Response>((_resolve, reject) =>
      setTimeout(() => reject(new Error('Translation fetch timeout')), FETCH_TIMEOUT_MS)
    );

    const response = await Promise.race([fetchPromise, timeoutPromise]);

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    const result: TranslationOverridesResponse = await response.json();

    if (!result.success || !result.data) {
      throw new Error('Failed to fetch translation overrides');
    }

    const overrides = result.data || {};

    systemLogger.debug('Fetched translation overrides from database', {
      languageCode,
      count: Object.keys(overrides).length,
    });

    return overrides;
  } catch (error) {
    const errorMessage = (error as Error).message;
    const isTimeout = errorMessage.includes('timeout');

    systemLogger.error('Failed to fetch translation overrides from database', {
      error,
      languageCode,
      isTimeout,
      willFallbackToCache: true
    });

    // Graceful fallback - return empty overrides (app will use static translations)
    return {};
  }
}

/**
 * Load translation overrides for a language
 *
 * Strategy:
 * 1. Normalize language code to base language (e.g., "en-US" → "en")
 * 2. Check localStorage cache first
 * 3. If cache is valid, return cached data
 * 4. If cache is missing or expired, fetch from database
 * 5. Cache the fetched data
 * 6. Return the overrides
 *
 * CRITICAL FIX: Removed admin detection that called getSession() before auth context initialized.
 * This eliminates circular dependency that caused 3-minute blank screen.
 * Admin users can manually refresh translations from admin panel to bypass cache.
 *
 * @param languageCode - ISO language code (e.g., 'en', 'es', 'fr', 'en-US')
 * @returns Translation overrides as key-value pairs
 */
export async function loadTranslationOverrides(
  languageCode: string
): Promise<Record<string, string>> {
  // Normalize language code to base language (e.g., "en-US" → "en")
  const normalized = normalizeLanguageCode(languageCode);

  // Try to get from cache first (for ALL users)
  const cachedOverrides = getCachedOverrides(normalized);
  if (cachedOverrides !== null) {
    systemLogger.debug('Using cached translation overrides', {
      languageCode: normalized,
      count: Object.keys(cachedOverrides).length
    });
    return cachedOverrides;
  }

  // Cache miss or expired - fetch fresh data from database
  try {
    const overrides = await fetchOverridesFromDatabase(normalized);

    // Cache the fetched data
    setCachedOverrides(normalized, overrides);

    return overrides;
  } catch (error) {
    systemLogger.error('Failed to load translation overrides, returning empty', {
      error,
      languageCode,
    });
    // Return empty object on error - fall back to static translations
    return {};
  }
}

/**
 * Clear translation overrides cache for a specific language
 *
 * @param languageCode - ISO language code (e.g., 'en', 'es', 'fr')
 */
export function clearTranslationCache(languageCode: string): void {
  try {
    const cacheKey = getCacheKey(languageCode);
    localStorage.removeItem(cacheKey);

    systemLogger.debug('Cleared translation override cache', { languageCode });
  } catch (error) {
    systemLogger.error('Failed to clear translation cache', {
      error,
      languageCode,
    });
  }
}

/**
 * Clear all translation overrides caches
 */
export function clearAllTranslationCaches(): void {
  try {
    const languages = ['en', 'es', 'fr', 'sv'];
    languages.forEach(lang => clearTranslationCache(lang));

    systemLogger.debug('Cleared all translation override caches');
  } catch (error) {
    systemLogger.error('Failed to clear all translation caches', { error });
  }
}

/**
 * Manually refresh translation overrides for a language
 *
 * Forces a fetch from database and updates cache
 *
 * @param languageCode - ISO language code (e.g., 'en', 'es', 'fr', 'en-US')
 * @returns Fresh translation overrides
 */
export async function refreshTranslationOverrides(
  languageCode: string
): Promise<Record<string, string>> {
  // Normalize language code to base language (e.g., "en-US" → "en")
  const normalized = normalizeLanguageCode(languageCode);

  // Clear existing cache
  clearTranslationCache(normalized);

  // Fetch fresh data
  const overrides = await fetchOverridesFromDatabase(normalized);

  // Cache the fresh data
  setCachedOverrides(normalized, overrides);

  systemLogger.debug('Refreshed translation overrides', {
    languageCode: normalized,
    count: Object.keys(overrides).length,
  });

  return overrides;
}

/**
 * Deep merge translation overrides with static translations
 *
 * Overrides completely replace values for matching keys
 *
 * @param staticTranslations - Base translations from JSON files
 * @param overrides - Database overrides as flat key-value pairs
 * @returns Merged translation object
 */
export function mergeTranslationOverrides(
  staticTranslations: Record<string, unknown>,
  overrides: Record<string, string>
): Record<string, unknown> {
  // Create a deep copy of static translations
  const merged = JSON.parse(JSON.stringify(staticTranslations)) as Record<
    string,
    unknown
  >;

  // Apply overrides using dot notation
  for (const [key, value] of Object.entries(overrides)) {
    const keys = key.split('.');
    let current = merged;

    // Navigate to the nested location
    for (let i = 0; i < keys.length - 1; i++) {
      const k = keys[i];
      if (!k) continue;

      if (!current[k] || typeof current[k] !== 'object') {
        current[k] = {};
      }
      current = current[k] as Record<string, unknown>;
    }

    // Set the final value
    const lastKey = keys[keys.length - 1];
    if (lastKey) {
      current[lastKey] = value;
    }
  }

  return merged;
}
